import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "../lib/api";
import { BadgePreview } from "../components/BadgePreview";
import { BadgeBack } from "../components/BadgeBack";
import { loadSettings, getVerifyBaseUrl } from "../lib/settings";
import { useCompany } from "../contexts/CompanyContext";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { ArrowLeft, Download, Pencil, Loader2, QrCode, Printer, FileText, UserMinus, UserCheck, Receipt, GraduationCap, HeartPulse, Megaphone, FileSignature, File as FileIcon, Crop as CropIcon, Mail, Copy, CheckCircle2, Undo2, IdCard, Wallet, KeyRound, Files, Plus, MoreHorizontal, Gavel } from "lucide-react";
import { downloadSingleBadgePDF, printSingleBadge, sanitizeFilename } from "../lib/pdf";
import { recordPrintEvent } from "../lib/printHistory";
import { EmployeeDocumentsPanel } from "../components/EmployeeDocumentsPanel";
import { CessationDialog, ReactivateDialog } from "../components/CessationDialog";
import { PortalAccessControl } from "../components/PortalAccessControl";
import { OPERATOR_PAGE_SECTIONS, OPERATOR_PAGES } from "../lib/operatorPages";
import { PhotoCropDialog } from "../components/PhotoCropDialog";
import { CredentialsLinkDialog } from "../components/CredentialsLinkDialog";
import { DocRequestDialog } from "../components/DocRequestDialog";
import { ContractHistoryPanel } from "../components/ContractHistoryPanel";
import { EmployeeAssignmentsPanel } from "../components/EmployeeAssignmentsPanel";
import { ComplianceHRPanel } from "../components/ComplianceHRPanel";
import { PendingChangesBanner } from "../components/PendingChangesBanner";
import { AdminEmployeeAdvancesPanel } from "../components/AdminEmployeeAdvancesPanel";
import { CATEGORY_LABELS, CATEGORY_ORDER } from "../lib/documents";
import { partTimePct } from "../lib/contract";
import { RequiredDocsCard } from "../components/RequiredDocsCard";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { toast } from "sonner";

const CATEGORY_ICONS = {
    busta_paga: Receipt,
    cu_730: FileText,
    contratto: FileSignature,
    certificato: GraduationCap,
    comunicazione: Megaphone,
    visita_medica: HeartPulse,
    documento_identita: IdCard,
    atti: Gavel,
    altro: FileIcon,
};

export default function EmployeeDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const company = useCompany();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [employee, setEmployee] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloading, setDownloading] = useState(false);
    const [printing, setPrinting] = useState(false);
    const [facsimile, setFacsimile] = useState(false);
    const [firmaDatore, setFirmaDatore] = useState(null);
    const [rotateBack, setRotateBack] = useState(false);
    const [cessationOpen, setCessationOpen] = useState(false);
    const [reactivateOpen, setReactivateOpen] = useState(false);
    const [docRequestOpen, setDocRequestOpen] = useState(false);
    const [cropOpen, setCropOpen] = useState(false);
    const [croppingPhoto, setCroppingPhoto] = useState(false);
    const [docsCategoryFilter, setDocsCategoryFilter] = useState("all");
    const [sendingCreds, setSendingCreds] = useState(false);
    const [credsResult, setCredsResult] = useState(null);
    const [advancesStats, setAdvancesStats] = useState({ count: 0, total: 0, toSign: 0 });
    const [groups, setGroups] = useState([]);
    const [includeClienteLogo, setIncludeClienteLogo] = useState(false);
    const [badgeFormat, setBadgeFormat] = useState("std"); // "std" 85,6×54 | "xl" 10×7 cm
    const [togglingOperator, setTogglingOperator] = useState(false);
    const frontRef = useRef(null);
    const backRef = useRef(null);
    const documentsRef = useRef(null);
    const documentsPanelRef = useRef(null);
    const advancesPanelRef = useRef(null);
    const verifyBase = getVerifyBaseUrl();

    const docsByCategory = useMemo(() => {
        const map = {};
        for (const d of documents) {
            const c = d.category || "altro";
            (map[c] = map[c] || []).push(d);
        }
        return map;
    }, [documents]);

    const latestBustaPaga = useMemo(() => {
        const list = docsByCategory.busta_paga || [];
        if (list.length === 0) return null;
        // Prefer the document with the latest period; fall back to uploaded_at
        const byPeriod = list
            .filter((d) => d.period)
            .sort((a, b) => (a.period < b.period ? 1 : -1));
        return byPeriod[0] || list.slice().sort((a, b) => (a.uploaded_at < b.uploaded_at ? 1 : -1))[0];
    }, [docsByCategory]);

    const scrollToDocuments = (category) => {
        setDocsCategoryFilter(category || "all");
        if (documentsRef.current) {
            documentsRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const scrollToAdvances = () => {
        if (advancesPanelRef.current) {
            advancesPanelRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    const toggleOperator = async (checked) => {
        setTogglingOperator(true);
        try {
            const { data } = await api.post(`/employees/${id}/operator`, { is_operator: checked });
            setEmployee((prev) => ({ ...prev, is_operator: data.is_operator }));
            toast.success(data.is_operator
                ? "Ruolo amministratore attivato: ora abilita qui sotto le pagine consentite"
                : "Ruolo amministratore disattivato");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore aggiornamento ruolo");
        } finally {
            setTogglingOperator(false);
        }
    };

    const toggleOperatorPage = async (path, enabled) => {
        const current = employee.operator_pages || [];
        const pages = enabled ? [...current, path] : current.filter((p) => p !== path);
        setEmployee((prev) => ({ ...prev, operator_pages: pages }));
        try {
            await api.post(`/employees/${id}/operator-pages`, { pages });
        } catch (e) {
            setEmployee((prev) => ({ ...prev, operator_pages: current }));
            toast.error("Errore nel salvataggio delle pagine abilitate");
        }
    };

    // Client logo resolution for the single-badge print: the GROUP logo prevails
    // over the personal one. Printing defaults to WITHOUT logo (toggle below badge).
    const clienteLogoInfo = useMemo(() => {
        if (!employee) return null;
        const withLogo = (groups || []).find(
            (g) => g.cliente_logo && (employee.group_ids || []).includes(g.id)
        );
        if (withLogo) return { logo: withLogo.cliente_logo, source: `gruppo "${withLogo.name}"` };
        if (employee.cliente_logo) return { logo: employee.cliente_logo, source: "personale" };
        return null;
    }, [employee, groups]);

    const badgeEmployee = useMemo(() => {
        if (!employee) return null;
        return {
            ...employee,
            cliente_logo: includeClienteLogo && clienteLogoInfo ? clienteLogoInfo.logo : null,
        };
    }, [employee, includeClienteLogo, clienteLogoInfo]);

    const fmtPeriodShort = (period) => {
        if (!period) return null;
        const MESI = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
        const m = /^(\d{4})-(\d{2})$/.exec(period);
        if (m) return `${MESI[parseInt(m[2], 10) - 1]} ${m[1]}`;
        return period;
    };

    useEffect(() => {
        (async () => {
            try {
                const [empRes, settings, docsRes, groupsRes] = await Promise.all([
                    api.get(`/employees/${id}`),
                    loadSettings(),
                    api.get(`/documents/by-employee/${id}`).catch(() => ({ data: [] })),
                    api.get("/groups").catch(() => ({ data: [] })),
                ]);
                setEmployee(empRes.data);
                setFirmaDatore(settings.firma_datore || null);
                setRotateBack(!!settings.rotate_back);
                setDocuments(docsRes.data || []);
                setGroups(groupsRes.data || []);
            } catch (e) {
                toast.error("Dipendente non trovato");
                navigate("/dipendenti");
            } finally {
                setLoading(false);
            }
        })();
    }, [id, navigate]);

    const handleDownload = async () => {
        if (!frontRef.current || !backRef.current || !employee) return;
        setDownloading(true);
        try {
            const suffix = facsimile ? "_FACSIMILE" : "";
            const fname = `tesserino_${sanitizeFilename(employee.cognome + "_" + employee.nome)}_${employee.matricola}${suffix}.pdf`;
            await downloadSingleBadgePDF(frontRef.current, backRef.current, fname, { facsimile, rotateBack, format: badgeFormat });
            if (!facsimile) recordPrintEvent("single_pdf", [employee.id], 1);
            toast.success(facsimile ? "FAC-SIMILE scaricato" : "Tesserino scaricato (fronte + retro)");
        } catch (e) {
            toast.error("Errore durante il download");
        } finally {
            setDownloading(false);
        }
    };

    const handleSendCredentials = async () => {
        if (!employee) return;
        if (!employee.email) {
            toast.error("Imposta prima l'email del dipendente in 'Credenziali'");
            return;
        }
        setSendingCreds(true);
        try {
            const { data } = await api.post(`/employees/${employee.id}/send-credentials`);
            setCredsResult(data);
            if (data.sent) {
                toast.success(`Email inviata a ${data.email}`);
            } else {
                toast.success("Link generato — copia e invia manualmente al dipendente");
            }
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore generazione link");
        } finally {
            setSendingCreds(false);
        }
    };

    const handlePrint = async () => {
        if (!frontRef.current || !backRef.current || !employee) return;
        setPrinting(true);
        try {
            await printSingleBadge(frontRef.current, backRef.current, { facsimile, rotateBack, format: badgeFormat });
            if (!facsimile) recordPrintEvent("single_print", [employee.id], 1);
            toast.success("Anteprima di stampa aperta");
        } catch (e) {
            toast.error("Errore durante la stampa");
        } finally {
            setPrinting(false);
        }
    };

    const handleRestoreOriginalPhoto = async () => {
        if (!employee?.photo_original) return;
        setCroppingPhoto(true);
        try {
            const { data } = await api.put(`/employees/${id}`, {
                photo: employee.photo_original,
            });
            setEmployee(data);
            toast.success("Foto originale ripristinata");
        } catch (e) {
            toast.error("Errore nel ripristino della foto");
        } finally {
            setCroppingPhoto(false);
        }
    };

    if (loading) {
        return (
            <div>
                <div className="flex h-[60vh] items-center justify-center">
                    <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
            </div>
        );
    }

    if (!employee) return null;

    const verifyUrl = `${verifyBase}/verify/${encodeURIComponent(employee.matricola)}`;

    return (
        <div data-testid="employee-detail-page">
            <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
                <Link
                    to="/dipendenti"
                    className="mb-4 inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#376DB3]"
                    data-testid="back-link"
                >
                    <ArrowLeft className="h-4 w-4" /> Torna ai dipendenti
                </Link>

                <div className="mb-4 flex flex-nowrap items-center gap-1.5 overflow-x-auto pb-1" data-testid="employee-actions">
                    {/* — Group: Profile / Identity — */}
                    <Link to={`/dipendenti/${employee.id}/modifica`}>
                        <Button variant="outline" size="sm" className="gap-1.5 whitespace-nowrap" data-testid="edit-button">
                            <Pencil className="h-4 w-4" /> Modifica
                        </Button>
                    </Link>
                    {employee.data_cessazione ? (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setReactivateOpen(true)}
                            className="gap-1.5 whitespace-nowrap border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                            data-testid="reactivate-button"
                        >
                            <UserCheck className="h-4 w-4" /> Riattiva
                        </Button>
                    ) : (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setCessationOpen(true)}
                            className="gap-1.5 whitespace-nowrap border-amber-300 text-amber-700 hover:bg-amber-50"
                            data-testid="mark-cessato-button"
                            title="Segna come cessato"
                        >
                            <UserMinus className="h-4 w-4" /> Cessazione
                        </Button>
                    )}

                    {/* — Separator — */}
                    <div className="mx-1 h-6 w-px shrink-0 bg-slate-200" aria-hidden />

                    {/* — Group: Documents — */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => documentsPanelRef.current?.openCredentials()}
                        className="gap-1.5 whitespace-nowrap"
                        data-testid="open-credentials-button"
                        title="Imposta email e password di accesso al portale dipendente"
                    >
                        <KeyRound className="h-4 w-4" /> Credenziali
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleSendCredentials}
                        disabled={sendingCreds}
                        className="gap-1.5 whitespace-nowrap border-[#376DB3] text-[#376DB3] hover:bg-[#376DB3]/5"
                        data-testid="send-credentials-button"
                        title="Genera e invia (o copia) il link di accesso al portale per il dipendente"
                    >
                        {sendingCreds ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Mail className="h-4 w-4" />
                        )}
                        Invia link
                    </Button>
                    <Button
                        size="sm"
                        onClick={() => documentsPanelRef.current?.openUpload()}
                        className="gap-1.5 whitespace-nowrap bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="open-upload-document"
                        title="Carica un singolo documento per questo dipendente"
                    >
                        <Plus className="h-4 w-4" /> Carica doc
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDocRequestOpen(true)}
                        className="gap-1.5 whitespace-nowrap"
                        data-testid="open-doc-request"
                        title="Richiedi al dipendente l'invio di un documento (es. certificato di residenza) con solleciti automatici ogni 3 giorni"
                    >
                        <FileText className="h-4 w-4" /> Richiedi doc
                    </Button>

                    {/* — Separator — */}
                    <div className="mx-1 h-6 w-px shrink-0 bg-slate-200" aria-hidden />

                    {/* — Group: Badge / Stampa — */}
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePrint}
                        disabled={printing}
                        className="gap-1.5 whitespace-nowrap"
                        data-testid="print-button"
                        title="Stampa il tesserino"
                    >
                        {printing ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Printer className="h-4 w-4" />
                        )}
                        Stampa
                    </Button>
                    <Button
                        size="sm"
                        onClick={handleDownload}
                        disabled={downloading}
                        className="gap-1.5 whitespace-nowrap bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="download-pdf-button"
                        title="Scarica il PDF del tesserino"
                    >
                        {downloading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Download className="h-4 w-4" />
                        )}
                        PDF
                    </Button>

                    {/* — More menu: less-frequent actions (Ritaglia, Ripristina, Caric. massivo, FAC-SIMILE) — */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant={facsimile ? "default" : "outline"}
                                size="sm"
                                className={`gap-1.5 whitespace-nowrap ${facsimile ? "bg-red-600 text-white hover:bg-red-700" : ""}`}
                                data-testid="more-actions-trigger"
                                title={facsimile ? "Modalità FAC-SIMILE attiva · clic per altre azioni" : "Altre azioni"}
                            >
                                <MoreHorizontal className="h-4 w-4" />
                                {facsimile ? "Altro · FAC-SIMILE" : "Altro"}
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56" data-testid="more-actions-menu">
                            {employee.photo && (
                                <DropdownMenuItem
                                    onSelect={(e) => { e.preventDefault(); setCropOpen(true); }}
                                    className="gap-2"
                                    data-testid="crop-photo-button"
                                >
                                    <CropIcon className="h-4 w-4 text-[#376DB3]" />
                                    <span>Ritaglia foto</span>
                                </DropdownMenuItem>
                            )}
                            {employee.photo_original && employee.photo_original !== employee.photo && (
                                <DropdownMenuItem
                                    onSelect={(e) => { e.preventDefault(); handleRestoreOriginalPhoto(); }}
                                    disabled={croppingPhoto}
                                    className="gap-2"
                                    data-testid="restore-original-photo-button"
                                >
                                    {croppingPhoto ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4 text-amber-600" />}
                                    <span>Ripristina foto originale</span>
                                </DropdownMenuItem>
                            )}
                            {(employee.photo || (employee.photo_original && employee.photo_original !== employee.photo)) && (
                                <DropdownMenuSeparator />
                            )}
                            <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); documentsPanelRef.current?.openBulk(); }}
                                className="gap-2"
                                data-testid="open-bulk-upload"
                            >
                                <Files className="h-4 w-4 text-[#376DB3]" />
                                <span>Caricamento massivo</span>
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                                onSelect={(e) => { e.preventDefault(); setFacsimile((v) => !v); }}
                                className="gap-2"
                                data-testid="toggle-facsimile-button"
                            >
                                <FileText className={`h-4 w-4 ${facsimile ? "text-red-600" : "text-slate-600"}`} />
                                <span>{facsimile ? "Disattiva FAC-SIMILE" : "Modalità FAC-SIMILE"}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Pending data update notifications */}
                <PendingChangesBanner employeeId={employee.id} />

                {/* Portal-style overview (mirror of what the employee sees) */}
                <section className="mb-6 overflow-hidden rounded-xl border border-slate-200 bg-gradient-to-br from-[#376DB3] to-[#15498F] p-6 text-white shadow-sm" data-testid="portal-overview">
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <div className="text-xs font-semibold uppercase tracking-widest text-white/70">
                                Matricola n° {employee.matricola}
                            </div>
                            <h1 className="mt-1 font-display text-3xl font-bold tracking-tight sm:text-4xl">
                                {employee.cognome} {employee.nome}
                            </h1>
                            <p className="mt-1 text-sm text-white/90">
                                {employee.qualifica}
                                {documents.length > 0 && (
                                    <> · <b>{documents.length}</b> document{documents.length === 1 ? "o" : "i"} disponibil{documents.length === 1 ? "e" : "i"}</>
                                )}
                            </p>
                        </div>
                        {employee.email && (
                            <div className="text-right text-xs text-white/80">
                                <div className="font-mono text-[11px]">{employee.email}</div>
                                {/@(gmail|googlemail)\.com$/i.test(employee.email) && employee.can_login && (
                                    <span
                                        className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-500/20 px-2 py-0.5 text-[10px] font-medium text-emerald-100 ring-1 ring-emerald-300/50"
                                        title="Questo dipendente può accedere al portale con Google Sign-In"
                                        data-testid="google-linked-badge"
                                    >
                                        <svg width="10" height="10" viewBox="0 0 48 48" aria-hidden="true" className="flex-shrink-0">
                                            <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                            <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                            <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                            <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                                        </svg>
                                        Google collegato
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                        {CATEGORY_ORDER.map((cat) => {
                            const Icon = CATEGORY_ICONS[cat] || FileIcon;
                            const count = docsByCategory[cat]?.length || 0;
                            const showLastBp = cat === "busta_paga" && latestBustaPaga;
                            const active = docsCategoryFilter === cat;
                            return (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => scrollToDocuments(active ? "all" : cat)}
                                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 transition-colors ${active ? "bg-white text-[#15498F] shadow-lg" : "bg-white/10 text-white hover:bg-white/20"}`}
                                    data-testid={`portal-overview-card-${cat}`}
                                >
                                    <Icon className="h-3.5 w-3.5 flex-shrink-0" />
                                    <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide">
                                        {CATEGORY_LABELS[cat]}
                                    </span>
                                    <span className="font-display text-sm font-bold font-mono-numbers">
                                        {count}
                                    </span>
                                    {showLastBp && (
                                        <span className={`whitespace-nowrap text-[10px] ${active ? "text-[#15498F]/70" : "text-white/70"}`} data-testid="last-busta-paga">
                                            · ult. <b>{fmtPeriodShort(latestBustaPaga.period) || "—"}</b>
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                        {/* Advances widget — admin view summary */}
                        <button
                            type="button"
                            onClick={scrollToAdvances}
                            className="inline-flex items-center gap-2 rounded-full bg-emerald-500/30 px-3 py-1.5 text-white transition-colors hover:bg-emerald-500/45"
                            data-testid="portal-overview-card-acconti"
                            title="Vedi gli acconti di questo dipendente"
                        >
                            <Wallet className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide">
                                Acconti
                            </span>
                            <span className="font-display text-sm font-bold font-mono-numbers">
                                {advancesStats.count}
                            </span>
                            {advancesStats.toSign > 0 && (
                                <span className="whitespace-nowrap text-[10px] text-amber-200" data-testid="acconti-to-sign">
                                    · <b>{advancesStats.toSign}</b> da firmare
                                </span>
                            )}
                        </button>
                    </div>
                </section>

                <div className="grid items-start gap-6 md:grid-cols-2">
                    {/* Badge preview + secondary info (QR + Azienda) under it */}
                    <div className="space-y-4">
                        <div className="rounded-lg border border-slate-200 bg-white p-8 shadow-sm">
                            <Tabs defaultValue="front">
                                <div className="mb-4 flex items-center justify-between">
                                    <TabsList>
                                        <TabsTrigger value="front" data-testid="tab-front">Fronte</TabsTrigger>
                                        <TabsTrigger value="back" data-testid="tab-back">Retro</TabsTrigger>
                                    </TabsList>
                                    <div className="flex items-center gap-0.5 rounded-md border border-slate-200 p-0.5">
                                        <button
                                            type="button"
                                            onClick={() => setBadgeFormat("std")}
                                            className={`rounded px-2 py-1 text-[11px] font-mono-numbers transition ${badgeFormat === "std" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                            title="Formato carta di credito 85,6 × 53,98 mm"
                                            data-testid="format-std-btn"
                                        >
                                            85,6×54
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setBadgeFormat("xl")}
                                            className={`rounded px-2 py-1 text-[11px] font-mono-numbers transition ${badgeFormat === "xl" ? "bg-[#376DB3] text-white" : "text-slate-500 hover:bg-slate-100"}`}
                                            title="Formato grande 10 × 7 cm"
                                            data-testid="format-xl-btn"
                                        >
                                            10×7 cm
                                        </button>
                                    </div>
                                </div>
                                {clienteLogoInfo && (
                                    <div
                                        className="mb-4 flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 px-3 py-2"
                                        data-testid="cliente-logo-toggle-row"
                                    >
                                        <div className="flex min-w-0 items-center gap-2 text-xs text-slate-600">
                                            <img
                                                src={clienteLogoInfo.logo}
                                                alt="Logo cliente"
                                                className="h-5 max-w-[48px] flex-shrink-0 object-contain"
                                            />
                                            <span className="truncate">
                                                Includi logo cliente{" "}
                                                <span className="text-slate-400">({clienteLogoInfo.source})</span>
                                            </span>
                                        </div>
                                        <Switch
                                            checked={includeClienteLogo}
                                            onCheckedChange={setIncludeClienteLogo}
                                            data-testid="toggle-cliente-logo"
                                        />
                                    </div>
                                )}
                                <TabsContent value="front" className="mt-0">
                                    <div className="flex justify-center">
                                        <BadgePreview employee={badgeEmployee} size={badgeFormat} ref={frontRef} />
                                    </div>
                                </TabsContent>
                                <TabsContent value="back" className="mt-0">
                                    <div className="flex justify-center">
                                        <BadgeBack
                                            employee={employee}
                                            firmaDatore={firmaDatore}
                                            verifyBaseUrl={verifyBase}
                                            size={badgeFormat}
                                            ref={backRef}
                                        />
                                    </div>
                                </TabsContent>
                            </Tabs>

                            {/* Always render the other side offscreen so the PDF capture works even on first click */}
                            <div className="print-only" aria-hidden>
                                <BadgePreview employee={badgeEmployee} size={badgeFormat} ref={frontRef} />
                                <BadgeBack
                                    employee={employee}
                                    firmaDatore={firmaDatore}
                                    verifyBaseUrl={verifyBase}
                                    size={badgeFormat}
                                    ref={backRef}
                                />
                            </div>
                        </div>

                        {/* Companion card: QR verify URL + company info — fills the gap under the badge */}
                        <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                            <div className="flex items-start gap-3 rounded-md bg-slate-50 p-3">
                                <QrCode className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#376DB3]" />
                                <div className="min-w-0 text-xs">
                                    <div className="font-semibold uppercase tracking-wide text-slate-700">
                                        URL Verifica Pubblica
                                    </div>
                                    <a
                                        href={verifyUrl}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="break-all text-[#376DB3] hover:underline"
                                        data-testid="verify-url-link"
                                    >
                                        {verifyUrl}
                                    </a>
                                </div>
                            </div>
                            <div className="mt-4 text-xs font-semibold uppercase tracking-widest text-slate-500">
                                Azienda
                            </div>
                            <div className="mt-2 space-y-1 text-sm text-slate-700">
                                <div className="font-display font-bold text-[#376DB3]">
                                    {company.ragione_sociale}
                                </div>
                                <div>{company.tagline}</div>
                                <div className="text-slate-600">{company.sede}</div>
                                <div className="text-slate-600 font-mono-numbers">P.IVA {company.p_iva}</div>
                            </div>
                        </div>

                        <RequiredDocsCard employeeId={id} refreshKey={documents.length} />
                    </div>

                    {/* Details */}
                    <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                        <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">
                            Dettagli
                        </div>
                        <h3 className="mt-1 font-display text-lg font-bold text-[#376DB3]">
                            Informazioni dipendente
                        </h3>
                        <dl className="mt-6 space-y-3 text-sm">
                            <Row label="Cognome e Nome" value={`${employee.cognome} ${employee.nome}`} />
                            <Row label="Codice Fiscale" value={employee.codice_fiscale} mono />
                            <Row label="Data di nascita" value={fmtDate(employee.data_nascita)} />
                            <Row label="Luogo di nascita" value={employee.luogo_nascita} />
                            <Row label="Residenza" value={fmtIndirizzo(employee)} />
                            <Row label="Qualifica" value={employee.qualifica} />
                            <Row label="Data di assunzione" value={fmtDate(employee.data_assunzione)} />
                            <Row label="Contratto" value={fmtContratto(employee)} />
                            {employee.contratto_scadenza && (
                                <Row
                                    label="Scadenza contratto"
                                    value={fmtDate(employee.contratto_scadenza)}
                                    tone={contractAlertTone(employee.contratto_scadenza)}
                                />
                            )}
                            <Row label="Matricola" value={employee.matricola} mono />
                            <Row label="Pos. INAIL TA" value={employee.pos_inail} mono />
                            <Row label="Pos. INPS TA" value={employee.pos_inps} mono />
                            {(employee.cell_personale || employee.telefono_fisso || employee.cell_aziendale) && (
                                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Contatti
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        {employee.cell_personale && (
                                            <div className="flex items-center justify-between gap-4">
                                                <dt className="text-xs uppercase tracking-wide text-slate-500">Cell. personale</dt>
                                                <dd className="font-mono-numbers font-medium text-[#376DB3]" data-testid="detail-cell-personale">
                                                    {employee.cell_personale}
                                                </dd>
                                            </div>
                                        )}
                                        {employee.telefono_fisso && (
                                            <div className="flex items-center justify-between gap-4">
                                                <dt className="text-xs uppercase tracking-wide text-slate-500">Telefono fisso</dt>
                                                <dd className="font-mono-numbers font-medium text-[#376DB3]" data-testid="detail-telefono-fisso">
                                                    {employee.telefono_fisso}
                                                </dd>
                                            </div>
                                        )}
                                        {employee.cell_aziendale && (
                                            <div className="flex items-center justify-between gap-4">
                                                <dt className="text-xs uppercase tracking-wide text-slate-500">Cell. aziendale</dt>
                                                <dd className="font-mono-numbers font-medium text-[#376DB3]" data-testid="detail-cell-aziendale">
                                                    {employee.cell_aziendale}
                                                </dd>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {(employee.username || employee.email) && (
                                <div className="mt-4 rounded-md border border-slate-200 bg-slate-50/60 p-3">
                                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                                        Accesso al portale
                                    </div>
                                    <div className="space-y-2 text-sm">
                                        {employee.username && (
                                            <div className="flex items-center justify-between gap-4">
                                                <dt className="text-xs uppercase tracking-wide text-slate-500">Username</dt>
                                                <dd className="font-mono font-medium text-[#376DB3]" data-testid="detail-username">
                                                    {employee.username}
                                                </dd>
                                            </div>
                                        )}
                                        {employee.email && (
                                            <div className="flex items-center justify-between gap-4">
                                                <dt className="text-xs uppercase tracking-wide text-slate-500">Email</dt>
                                                <dd className="font-mono text-xs font-medium text-[#376DB3]" data-testid="detail-email">
                                                    {employee.email}
                                                </dd>
                                            </div>
                                        )}
                                        {isAdmin && (
                                            <div className="flex items-center justify-between gap-4 border-t border-slate-200 pt-2">
                                                <dt>
                                                    <div className="text-xs uppercase tracking-wide text-slate-500">Ruolo amministratore</div>
                                                    <div className="mt-0.5 text-[10px] leading-tight text-slate-400">
                                                        Accesso al portale admin (solo pagine abilitate)
                                                    </div>
                                                </dt>
                                                <dd>
                                                    <Switch
                                                        checked={!!employee.is_operator}
                                                        onCheckedChange={toggleOperator}
                                                        disabled={togglingOperator || !employee.can_login}
                                                        title={!employee.can_login ? "Attiva prima l'accesso al portale (credenziali)" : ""}
                                                        data-testid="operator-role-toggle"
                                                    />
                                                </dd>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                            {employee.data_cessazione && (
                                <>
                                    <div className="flex items-center justify-between gap-4">
                                        <dt className="text-xs uppercase tracking-wide text-slate-500">Data cessazione</dt>
                                        <dd className="flex items-center gap-1.5 text-sm font-medium text-amber-700">
                                            {fmtDate(employee.data_cessazione)}
                                            <button
                                                type="button"
                                                onClick={() => setCessationOpen(true)}
                                                className="flex h-6 w-6 items-center justify-center rounded text-amber-600 hover:bg-amber-50 hover:text-amber-800"
                                                title="Modifica data/motivo di cessazione"
                                                data-testid="edit-cessation-button"
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        </dd>
                                    </div>
                                    <Row label="Motivo cessazione" value={employee.motivo_cessazione} tone="cessato" />
                                    {isAdmin && <PortalAccessControl employee={employee} onUpdated={(data) => setEmployee(data)} />}
                                </>
                            )}
                        </dl>
                    </div>
                </div>

                {/* Operator page permissions — full-width horizontal panel */}
                {isAdmin && employee.is_operator && (
                    <div className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="operator-pages-panel">
                        <div className="mb-1 flex flex-wrap items-center justify-between gap-3">
                            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-700">
                                Pagine operative abilitate
                            </h2>
                            <span className="rounded-full bg-[#376DB3]/10 px-2.5 py-0.5 text-xs font-semibold text-[#376DB3]" data-testid="operator-pages-count">
                                {(employee.operator_pages || []).length}/{OPERATOR_PAGES.length}
                            </span>
                        </div>
                        <p className="mb-4 text-xs leading-tight text-slate-400">
                            Le pagine non abilitate spariscono dal menu dell'operatore e sono bloccate anche da link diretto.
                        </p>
                        <div className="flex flex-wrap gap-x-10 gap-y-4">
                            {OPERATOR_PAGE_SECTIONS.map((sec) => (
                                <div key={sec.title} className="max-w-full">
                                    <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-slate-400">{sec.title}</div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {sec.items.map((p) => {
                                            const on = (employee.operator_pages || []).includes(p.key);
                                            return (
                                                <label
                                                    key={p.key}
                                                    className={`flex cursor-pointer items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] transition-colors ${on
                                                        ? "border-[#376DB3]/40 bg-[#376DB3]/10 font-semibold text-[#376DB3]"
                                                        : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        className="h-3 w-3 accent-[#376DB3]"
                                                        checked={on}
                                                        onChange={(e) => toggleOperatorPage(p.key, e.target.checked)}
                                                        data-testid={`operator-page-${p.key.replace(/[/:]/g, "-").replace(/^-/, "") || "dashboard"}`}
                                                    />
                                                    {p.label}
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Contract history */}
                <div className="mt-8">
                    <ContractHistoryPanel
                        employee={employee}
                        onContractSaved={() => documentsPanelRef.current?.refresh()}
                        onUpdated={async () => {
                            const { data } = await api.get(`/employees/${id}`);
                            setEmployee(data);
                        }}
                    />
                </div>

                {/* Worksite / activity assignments (planning) */}
                <div className="mt-8">
                    <EmployeeAssignmentsPanel employee={employee} />
                </div>

                {/* Employee advances (read-only summary, admin view) */}
                <div ref={advancesPanelRef} className="mt-8">
                    <AdminEmployeeAdvancesPanel
                        employeeId={employee.id}
                        onStatsChange={setAdvancesStats}
                    />
                </div>

                {/* HR Compliance overview — consent + download/ack stats */}
                <div className="mt-8">
                    <ComplianceHRPanel employee={employee} />
                </div>

                {/* Employee documents panel */}
                <div ref={documentsRef} className="mt-8 rounded-lg border border-slate-200 bg-white p-6 shadow-sm" data-testid="documents-panel-wrap">
                    <EmployeeDocumentsPanel
                        ref={documentsPanelRef}
                        employee={employee}
                        categoryFilter={docsCategoryFilter}
                        onCategoryFilterChange={setDocsCategoryFilter}
                        actionsAtTop
                    />
                </div>
            </main>

            <DocRequestDialog
                open={docRequestOpen}
                onClose={() => setDocRequestOpen(false)}
                employeeId={id}
                employeeName={`${employee?.nome || ""} ${employee?.cognome || ""}`.trim()}
            />
            <CessationDialog
                open={cessationOpen}
                onOpenChange={setCessationOpen}
                employee={employee}
                onSaved={async () => {
                    const { data } = await api.get(`/employees/${id}`);
                    setEmployee(data);
                }}
            />
            <ReactivateDialog
                open={reactivateOpen}
                onOpenChange={setReactivateOpen}
                employee={employee}
                onSaved={async () => {
                    const { data } = await api.get(`/employees/${id}`);
                    setEmployee(data);
                }}
            />
            <PhotoCropDialog
                open={cropOpen}
                src={employee?.photo_original || employee?.photo}
                onClose={() => setCropOpen(false)}
                onSave={async (dataUrl) => {
                    setCroppingPhoto(true);
                    try {
                        // First-time crop on a legacy photo (no original stored yet)?
                        // → snapshot the current photo as the original BEFORE applying the new crop,
                        // so the admin can always restore the un-cropped version.
                        const payload = { photo: dataUrl };
                        if (!employee?.photo_original && employee?.photo) {
                            payload.photo_original = employee.photo;
                        }
                        const { data } = await api.put(`/employees/${id}`, payload);
                        setEmployee(data);
                        setCropOpen(false);
                        toast.success("Foto aggiornata");
                    } catch (e) {
                        toast.error("Errore salvataggio foto");
                    } finally {
                        setCroppingPhoto(false);
                    }
                }}
            />
            <CredentialsLinkDialog
                result={credsResult}
                employee={employee}
                onClose={() => setCredsResult(null)}
            />
        </div>
    );
}

const Row = ({ label, value, mono, tone }) => {
    const valueColor =
        tone === "cessato" ? "text-amber-700"
        : tone === "scaduto" ? "text-red-700"
        : tone === "scadenza-imminente" ? "text-amber-700"
        : "text-[#376DB3]";
    return (
        <div className="flex items-center justify-between gap-4 border-b border-slate-100 pb-2 last:border-b-0">
            <dt className="text-xs uppercase tracking-wide text-slate-500">{label}</dt>
            <dd
                className={`text-sm font-medium ${valueColor} ${mono ? "font-mono-numbers" : ""}`}
                data-testid={`detail-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
                {value || "—"}
                {tone === "scaduto" && <span className="ml-2 rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">SCADUTO</span>}
                {tone === "scadenza-imminente" && <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">IN SCADENZA</span>}
            </dd>
        </div>
    );
};

const contractAlertTone = (dateStr) => {
    if (!dateStr) return null;
    const exp = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((exp - today) / 86400000);
    if (days < 0) return "scaduto";
    if (days <= 10) return "scadenza-imminente";
    return null;
};

const fmtDate = (s) => {
    if (!s) return "—";
    const p = s.split("-");
    if (p.length !== 3) return s;
    return `${p[2]}/${p[1]}/${p[0]}`;
};

const fmtContratto = (employee) => {
    const tipo = employee.contratto_tipo;
    const ore = employee.contratto_ore;
    const oreFormatted = ore != null && ore !== ""
        ? Number(ore).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : null;
    const pct = partTimePct(tipo, ore);
    if (!tipo && !oreFormatted) return "—";
    if (tipo && oreFormatted) return `${tipo} · ${oreFormatted} ore${pct ? ` (${pct})` : ""}`;
    return tipo || `${oreFormatted} ore`;
};

const fmtIndirizzo = (employee) => {
    const parts = [];
    if (employee.indirizzo) parts.push(employee.indirizzo);
    const cityParts = [];
    if (employee.cap) cityParts.push(employee.cap);
    if (employee.citta) cityParts.push(employee.citta);
    if (employee.provincia) cityParts.push(`(${employee.provincia})`);
    if (cityParts.length) parts.push(cityParts.join(" "));
    return parts.length ? parts.join(", ") : "—";
};
